
import React, { PureComponent } from 'react';

import { createToolbarEvent, sendAnalytics } from '../../../analytics';
import { getWindowWidth } from '../../../base/environment/utils';
import { translate } from '../../../base/i18n';
import { IconMenu } from '../../../base/icons';
import { InlineDialogWithoutAnalytics } from '../../../base/react/components/web/InlineDialog';
import { connect } from '../../../base/redux';

import ToolbarButton from './ToolbarButton';

/**
 * The type of the React {@code Component} props of {@link OverflowMenuButton}.
 */
type Props = {

    /**
     * A child React Element to display within {@code InlineDialog}.
     */
    children: React$Node,

    /**
     * Whether or not the OverflowMenu popover should display.
     */
    isOpen: boolean,

    /**
     * Calback to change the visibility of the overflow menu.
     */
    onVisibilityChange: Function,

    /**
     * Whether to display the OverflowMenu as a drawer.
     */
    overflowDrawer: boolean,

    /**
     * Invoked to obtain translated strings.
     */
    t: Function
};

/**
 * A React {@code Component} for opening or closing the {@code OverflowMenu}.
 *
 * @augments Component
 */
class OverflowMenuButton extends PureComponent<Props> {
    /**
     * Initializes a new {@code OverflowMenuButton} instance.
     *
     * @param {Object} props - The read-only properties with which the new
     * instance is to be initialized.
     */
    constructor(props: Props) {
        super(props);

        // Bind event handlers so they are only bound once per instance.
        this._onCloseDialog = this._onCloseDialog.bind(this);
        this._onToggleDialogVisibility
            = this._onToggleDialogVisibility.bind(this);
    }

    /**
     * Implements React's {@link Component#render()}.
     *
     * @inheritdoc
     * @returns {ReactElement}
     */
    render() {
        const { children, isOpen, _conferenceView } = this.props;

        return (

            <InlineDialogWithoutAnalytics
                content = { children }
                isOpen = { isOpen }
                onClose = { this._onCloseDialog }
                placement = { _conferenceView === 'float' || getWindowWidth() <= 612 ? 'left-end' : 'top-end' }>
                {this._renderToolbarButton()}
            </InlineDialogWithoutAnalytics>
        );
    }

    _renderToolbarButton: () => React$Node;

    /**
     * Renders the actual toolbar overflow menu button.
     *
     * @returns {ReactElement}
     */
    _renderToolbarButton() {
        const { isOpen, t, position } = this.props;

        return (
            <ToolbarButton
                accessibilityLabel =
                    { t('toolbar.accessibilityLabel.moreActions') }
                customClass = 'overflow-menu-btn'
                icon = { IconMenu }
                onClick = { this._onToggleDialogVisibility }
                position = { position }
                toggled = { isOpen }
                tooltip = { t('toolbar.moreActions') } />
        );
    }

    _onCloseDialog: () => void;

    /**
     * Callback invoked when {@code InlineDialog} signals that it should be
     * closed. We guard against Atlaskit's built-in outside-click detection
     * firing when the user actually clicked *inside* the overflow menu (this
     * happens because CSS `transform` moves the menu visually but Atlaskit
     * still uses the original, pre-transform DOM bounds for hit-testing).
     *
     * @param {Object} closeData - Data passed by InlineDialog, contains event.
     * @private
     * @returns {void}
     */
    _onCloseDialog(closeData) {
        // Atlaskit passes { isOpen, event } to onClose.
        const event = closeData && (closeData.event || closeData);

        if (event && event.target) {
            // If the click was inside the overflow menu, do NOT close.
            const insideMenu = event.target.closest(
                '.overflow-menu, .overflow-menu-item, .overflow-menu-item-icon, .overflow-menu-item-text, .rows, .action-btn1, .overflow-menu-btn'
            );

            if (insideMenu) {
                return;
            }
        }

        this.props.onVisibilityChange(false);
    }

    _onToggleDialogVisibility: () => void;

    /**
     * Callback invoked to signal that an event has occurred that should change
     * the visibility of the {@code InlineDialog} component.
     *
     * @private
     * @returns {void}
     */
    _onToggleDialogVisibility() {
        sendAnalytics(createToolbarEvent('overflow'));

        this.props.onVisibilityChange(!this.props.isOpen);
    }
}

/**
 * Maps (parts of) the Redux state to the associated props for the
 * {@code OverflowMenuButton} component.
 *
 * @param {Object} state - The Redux state.
 * @returns {Props}
 */
function mapStateToProps(state) {
    const { overflowDrawer } = state['features/toolbox'];
    const _conferenceView = state['features/base/conference'].conferenceView;

    return {
        overflowDrawer,
        _conferenceView
    };
}

// Memoize the component to prevent unnecessary rerenders
const MemoizedOverflowMenuButton = React.memo(OverflowMenuButton, (prevProps, nextProps) =>

// Custom comparison function to prevent rerenders when props haven't meaningfully changed

    prevProps.isOpen === nextProps.isOpen
        && prevProps.overflowDrawer === nextProps.overflowDrawer
        && prevProps._conferenceView === nextProps._conferenceView
        && prevProps.position === nextProps.position

        // Compare children by reference - if the content hasn't changed, don't rerender
        && prevProps.children === nextProps.children

);

export default translate(connect(mapStateToProps)(MemoizedOverflowMenuButton));
